import React from "react";

const PrivacyPolicy = () => {
  return (
    <div className="bg-white text-black px-6 py-16 max-w-3xl mx-auto font-sans">
      <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>

      <p className="text-sm text-black/80 mb-4">Last Updated: April 21, 2025</p>

      <p className="text-sm text-black/80 mb-4">
        FullPlateNow LLC (“FullPlateNow,” “we,” “us,” or “our”) is committed to
        protecting the privacy of independent restaurants (“Restaurants,” “you,”
        or “your”) that use our business-to-business software-as-a-service
        platform (the “Platform”) and website located at [www.fullplatenow.com]
        (the “Website”) (collectively, the “Services”). This Privacy Policy
        explains how we collect, use, disclose, and protect your information in
        connection with the Services. The Services are designed to manage your
        entire presence on third-party delivery services, including but not
        limited to DoorDash, Grubhub, Uber Eats, and Postmates (collectively,
        “Delivery Services”), including setting menu prices, managing orders,
        and providing analytics. By accessing or using the Services, you consent
        to the practices described in this Privacy Policy and agree to be bound
        by our Terms and Conditions, which are incorporated herein by reference.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">
        1. Scope of This Privacy Policy
      </h2>
      <p className="text-sm mb-4">
        This Privacy Policy applies to information we collect from Restaurants
        through the Services, including business-related data necessary to
        manage your operations on Delivery Services. FullPlateNow does not
        directly interact with or collect personal information from end
        customers; all orders are placed through Delivery Services, and any
        customer data processed by FullPlateNow is managed on behalf of the
        Delivery Services as part of our service to you. This Privacy Policy
        does not apply to the practices of third parties, including Delivery
        Services, whose privacy practices are governed by their own policies.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">
        2. Information We Collect
      </h2>
      <h3 className="text-base font-semibold mt-4 mb-2">
        2.1 Information You Provide
      </h3>
      <p className="text-sm mb-4">
        We collect the following information directly from you when you register
        for an account, use the Services, or communicate with us:
      </p>
      <ul className="text-sm mb-4 list-disc pl-5">
        <li>
          <strong>Business Registration Details:</strong> Information about your
          Restaurant, such as legal business name, address, tax identification
          number, and licensing details, to verify your eligibility and
          facilitate payouts.
        </li>
        <li>
          <strong>Contact Information:</strong> Names, email addresses, and
          phone numbers of authorized representatives to communicate updates and
          provide support.
        </li>
        <li>
          <strong>Payment Data:</strong> Bank account details for direct
          deposits of your earnings (base menu price) from Delivery Services.
        </li>
        <li>
          <strong>Menu and Pricing Data:</strong> Menu items, base prices,
          descriptions, and images you provide, which we use to manage your
          presence on Delivery Services, including applying a 50-60% upcharge as
          our service fee.
        </li>
        <li>
          <strong>Order Management Data:</strong> Information related to orders
          processed through Delivery Services, including order details (e.g.,
          items, quantities, statuses) and communications with Delivery Services
          representatives.
        </li>
      </ul>

      <h3 className="text-base font-semibold mt-4 mb-2">
        2.2 Information We Collect Automatically
      </h3>
      <p className="text-sm mb-4">
        We collect the following information automatically as you use the
        Services:
      </p>
      <ul className="text-sm mb-4 list-disc pl-5">
        <li>
          <strong>Usage Analytics:</strong> Data about how you interact with the
          Services, such as pages visited, features used, and time spent on the
          Platform, to improve functionality and user experience.
        </li>
        <li>
          <strong>Device and Log Information:</strong> Technical details like IP
          address, browser type, device identifiers, and log files to ensure
          security and troubleshoot issues.
        </li>
      </ul>

      <h3 className="text-base font-semibold mt-4 mb-2">
        2.3 Information from Third Parties
      </h3>
      <p className="text-sm mb-4">
        We may receive information from Delivery Services, including order data,
        delivery statuses, and financial transactions, to provide the Services.
        This information is processed on your behalf to manage your operations.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">
        3. How We Use Your Information
      </h2>
      <p className="text-sm mb-4">
        We use the information we collect for the following purposes:
      </p>
      <ul className="text-sm mb-4 list-disc pl-5">
        <li>
          <strong>Service Provision:</strong> To manage your Restaurant’s
          presence on Delivery Services, including setting upcharged menu
          prices, syncing menus, processing orders, and facilitating
          communication with Delivery Services representatives or drivers.
        </li>
        <li>
          <strong>Financial Management:</strong> To calculate and collect our
          service fee (50-60% upcharge paid by customers through Delivery
          Services), reconcile earnings across platforms, and process direct
          deposits of your base menu price earnings.
        </li>
        <li>
          <strong>Analytics and Reporting:</strong> To generate daily, weekly,
          and monthly reports on order volume, revenue, and performance across
          Delivery Services, helping you monitor your business.
        </li>
        <li>
          <strong>Communication:</strong> To send notifications about new
          orders, driver updates, or messages from Delivery Services, and to
          provide support for your inquiries.
        </li>
        <li>
          <strong>Platform Improvement:</strong> To analyze usage patterns and
          improve the functionality, performance, and user experience of the
          Services.
        </li>
        <li>
          <strong>Legal Compliance:</strong> To comply with applicable laws,
          regulations, or legal processes, including tax reporting related to
          the upcharge service fee.
        </li>
      </ul>
      <p className="text-sm mb-4">
        We do not sell your information to third parties for marketing purposes.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">
        4. How We Share Your Information
      </h2>
      <h3 className="text-base font-semibold mt-4 mb-2">
        4.1 With Delivery Services
      </h3>
      <p className="text-sm mb-4">
        We share necessary data with Delivery Services to manage your
        operations, including:
      </p>
      <ul className="text-sm mb-4 list-disc pl-5">
        <li>
          Menu and pricing data, including upcharged prices, to list your items
          on their platforms.
        </li>
        <li>
          Order details, statuses, and communications to facilitate fulfillment
          and delivery.
        </li>
        <li>
          Financial data to reconcile earnings and collect our service fee.
        </li>
      </ul>

      <h3 className="text-base font-semibold mt-4 mb-2">
        4.2 With Service Providers
      </h3>
      <p className="text-sm mb-4">
        We may share your information with third-party service providers who
        assist us in operating the Services, including:
      </p>
      <ul className="text-sm mb-4 list-disc pl-5">
        <li>Payment processors to handle direct deposits of your earnings.</li>
        <li>Analytics providers to track usage and improve the Platform.</li>
        <li>
          IT and security providers to maintain the integrity and security of
          the Services.
        </li>
      </ul>
      <p className="text-sm mb-4">
        These providers are bound by contractual obligations to protect your
        information and use it only for the purposes for which it was shared.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">
        4.3 For Legal Reasons
      </h3>
      <p className="text-sm mb-4">
        We may disclose your information if required by law, regulation, or
        legal process, or to protect the rights, property, or safety of
        FullPlateNow, our users, or the public.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">
        4.4 Business Transfers
      </h3>
      <p className="text-sm mb-4">
        In the event of a merger, acquisition, or sale of all or part of
        FullPlateNow’s assets, your information may be transferred to the
        acquiring entity, subject to the same privacy protections outlined in
        this Privacy Policy.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">5. Data Security</h2>
      <p className="text-sm mb-4">
        We implement industry-standard measures to protect your information,
        including encryption, secure storage, and access controls. However, no
        method of transmission or storage is completely secure, and we cannot
        guarantee absolute security. FullPlateNow is not liable for any
        unauthorized access, disclosure, or loss of your information unless
        caused by our gross negligence or willful misconduct. In the event of a
        data breach, we will notify you as required by applicable law.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">6. Data Retention</h2>
      <p className="text-sm mb-4">
        We retain your information for as long as necessary to provide the
        Services, fulfill our contractual obligations, and comply with legal
        requirements. For example:
      </p>
      <ul className="text-sm mb-4 list-disc pl-5">
        <li>
          Account and business registration details are retained for the
          duration of your use of the Services and for a period thereafter as
          required by law (e.g., tax reporting).
        </li>
        <li>
          Order and financial data are retained to support analytics, reporting,
          and payout reconciliation, typically for a minimum of seven (7) years
          to comply with financial regulations.
        </li>
        <li>
          Usage analytics may be retained in aggregated, anonymized form
          indefinitely to improve the Platform.
        </li>
      </ul>
      <p className="text-sm mb-4">
        Upon termination of your Account, we will delete or anonymize your
        information, except as required to comply with legal obligations or
        resolve disputes.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">
        7. Your Rights and Choices
      </h2>
      <h3 className="text-base font-semibold mt-4 mb-2">
        7.1 Access and Correction
      </h3>
      <p className="text-sm mb-4">
        You may access and update your Account information (e.g., contact
        details, payment data) at any time through the Settings section of the
        Platform. If you need assistance, please contact us at
        [support@fullplatenow.com].
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">7.2 Data Deletion</h3>
      <p className="text-sm mb-4">
        You may request deletion of your Account and associated data by
        contacting us at [support@fullplatenow.com]. We will comply with your
        request, subject to any legal obligations to retain certain information
        (e.g., for tax purposes).
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">
        7.3 Opt-Out of Communications
      </h3>
      <p className="text-sm mb-4">
        You may opt out of non-essential communications (e.g., updates,
        newsletters) by adjusting your notification preferences in the Settings
        section or by following the unsubscribe instructions in our emails.
        Essential communications (e.g., order notifications, account updates)
        cannot be opted out of while using the Services.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">
        8. International Data Transfers
      </h2>
      <p className="text-sm mb-4">
        FullPlateNow operates primarily in the United States, but your
        information may be transferred to and processed in other countries where
        our service providers or Delivery Services are located. We ensure that
        such transfers comply with applicable data protection laws, using
        safeguards such as standard contractual clauses or other approved
        mechanisms to protect your information. By using the Services, you
        consent to the transfer, processing, and storage of your information in
        jurisdictions outside your country of residence.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">
        9. Compliance with Privacy Laws
      </h2>
      <h3 className="text-base font-semibold mt-4 mb-2">
        9.1 General Data Protection Regulation (GDPR)
      </h3>
      <p className="text-sm mb-4">
        If you are located in the European Economic Area (EEA), you may have
        additional rights under the GDPR, including the right to access,
        correct, delete, or restrict the processing of your data, as well as the
        right to data portability and to object to processing. To exercise these
        rights, please contact us at [support@fullplatenow.com]. We process your
        information under the legal basis of contractual necessity to provide
        the Services and legitimate interest to improve the Platform.
      </p>

      <h3 className="text-base font-semibold mt-4 mb-2">
        9.2 California Consumer Privacy Act (CCPA)
      </h3>
      <p className="text-sm mb-4">
        If you are a California resident, you may have rights under the CCPA,
        including the right to know what information we collect, the right to
        request deletion, and the right to opt out of the sale of your
        information. FullPlateNow does not sell your information, and as a B2B
        service, our primary data collection relates to business information,
        which may be exempt from certain CCPA provisions. However, you may still
        exercise applicable rights by contacting us at
        [support@fullplatenow.com].
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">
        10. Children’s Privacy
      </h2>
      <p className="text-sm mb-4">
        The Services are not intended for use by individuals under the age of
        18. We do not knowingly collect personal information from children under
        13. If we become aware that we have collected such information, we will
        take steps to delete it in accordance with applicable laws, such as the
        Children’s Online Privacy Protection Act (COPPA).
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">
        11. Changes to This Privacy Policy
      </h2>
      <p className="text-sm mb-4">
        We may update this Privacy Policy from time to time to reflect changes
        in our practices, legal requirements, or operational needs. We will
        notify you of material changes by posting the updated policy on the
        Website or by sending a notification to the email address associated
        with your Account. Your continued use of the Services after such changes
        constitutes your acceptance of the updated Privacy Policy.
      </p>

      <h2 className="text-lg font-semibold mt-6 mb-2">
        12. Contact Information
      </h2>
      <p className="text-sm mb-4">
        For questions, concerns, or to exercise your privacy rights, please
        contact us at:
      </p>
      <p className="text-sm mb-4">
        FullPlateNow LLC <br />
        Email:{" "}
        <a href="mailto:support@fullplatenow.com" className="underline">
          support@fullplatenow.com
        </a>{" "}
        <br />
        Address: 8 The Green, Dover, DE 19901
      </p>

      <p className="text-xs text-black/50 mt-10">
        Effective date: April 21, 2025
      </p>
    </div>
  );
};

export default PrivacyPolicy;
